import type { RefObject } from "react";
import { Suspense, useRef, useState } from "react";
import type { Toast } from "react-hot-toast";
import { Toaster } from "react-hot-toast";
import { useMetaTags } from "react-metatags-hook";
import { Outlet, useLocation } from "react-router";
import GlobalSidebar from "@/components/layout/sidebar/global-sidebar";
import DevModal from "@/components/shared/dev-modal";
import useScrollRestoration from "@/hooks/useScrollRestoration";
import { defaultOpenGraphImage, isTestMode, siteDescription } from "@/util/const";
import Footer from "./footer.tsx";
import Header from "./header";

export default function Layout() {
  const currentURL = window.location.href;
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const childRef = useRef<HTMLDivElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useMetaTags(
    {
      title: !isTestMode ? "St. Jude Play Live Tools" : "Play Live Tools",
      description: siteDescription,
      openGraph: {
        type: "website",
        image: defaultOpenGraphImage,
        url: currentURL,
        description: siteDescription,
        site_name: !isTestMode ? "St. Jude Play Live Tools" : "Play Live Tools",
      },
      twitter: {
        card: "summary_large_image",
      },
    },
    [siteDescription, defaultOpenGraphImage, currentURL],
  );

  useScrollRestoration({
    scrollRef: scrollRef as RefObject<HTMLElement>,
    childRef: childRef as RefObject<HTMLElement>,
    pathname: location.pathname,
  });

  function setScrollRef(div: HTMLDivElement | null) {
    scrollRef.current = div;
  }

  function setChildRef(div: HTMLDivElement | null) {
    childRef.current = div;
  }

  function onLogoClick() {
    setDrawerOpen(false);
  }

  function onMenuButtonClick() {
    setDrawerOpen((prev) => !prev);
  }

  return (
    <>
      <Toaster>
        {(t: Toast) => (
          <div
            role="alert"
            className={`${
              t.visible ? "animate-enter" : "animate-leave"
            } alert max-w-5xl ${t.className}`}
          >
            {t.message as React.ReactNode}
          </div>
        )}
      </Toaster>
      <div className="bg-base-100 flex flex-col w-full h-full overflow-hidden relative">
        <DevModal />
        <GlobalSidebar isDrawerOpen={drawerOpen} onDrawerClose={() => setDrawerOpen(false)}>
          <div
            className="scroll-ref w-full grow flex flex-col overflow-y-auto overflow-x-hidden"
            ref={setScrollRef}
          >
            <Header
              onMenuClick={onMenuButtonClick}
              isDrawerOpen={drawerOpen}
              onLogoClick={onLogoClick}
            />
            <div className="h-full flex flex-col scrollbar-gutter-fix">
              <div className="grow" ref={setChildRef}>
                <Suspense fallback={null}>
                  <Outlet />
                </Suspense>
              </div>
              <Footer constrictWidth />
            </div>
          </div>
        </GlobalSidebar>
      </div>
    </>
  );
}
